import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { Button, Modal, Text, useTheme } from '@querodelivery/uni-react';
import { toast } from 'react-toastify';
import styles from './order-proxy-phone-modal.module.scss';
import type { DeliveryProxy } from '@/_features/deliveries/componentes/order-proxy-phone/order-proxy-phone';
import { proxyPhoneLabel } from '@/Utils/FnUtils';

interface OrderProxyPhoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  proxy: DeliveryProxy;
  isMobile?: boolean;
}

export function OrderProxyPhoneModal({
  isOpen,
  onClose,
  proxy,
  isMobile = false,
}: OrderProxyPhoneModalProps) {
  const { color } = useTheme();
  const formattedPhone = proxyPhoneLabel(proxy.phone) || proxy.phone;

  async function handleCopyPhone() {
    try {
      await navigator.clipboard.writeText(proxy.phone);
      toast.success('Telefone copiado!');
    } catch {
      toast.error('Erro ao copiar telefone!');
    }
  }

  async function handleCopyProxyId() {
    try {
      await navigator.clipboard.writeText(proxy.proxyId);
      toast.success('Código copiado!');
    } catch {
      toast.error('Erro ao copiar código!');
    }
  }

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <Modal
      isOpen
      onClose={onClose}
      header={
        <Text as="h2" variant="headingMd" tone="highlight" fontWeight="bold">
          Como ligar para o cliente
        </Text>
      }
      width={isMobile ? undefined : '480px'}
    >
      <div className={styles.modalContent}>
        <div className={styles.infoContainer}>
          <FontAwesomeIcon
            icon={faCircleExclamation}
            color={color['color-action-fill']}
          />
          <Text as="p" variant="bodyXs" tone="supporting" fontWeight="medium">
            Por motivos de segurança, o número do cliente não é exibido.
          </Text>
        </div>
        <div className={styles.steps}>
          <div>
            <Text
              as="span"
              variant="headingSm"
              fontWeight="semibold"
              fontSize="sm"
              lineHeight="lg"
            >
              1. Ligue para o telefone abaixo
            </Text>
            <div className={styles.textContainer}>
              <Text fontWeight="medium" fontSize="sm" as="span">
                {formattedPhone}
              </Text>
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopyPhone}
                aria-label="Copiar telefone"
              >
                <FontAwesomeIcon
                  icon={faClone}
                  color={color['color-base-border-2']}
                  fontSize="16px"
                />
              </button>
            </div>
            <div className={styles.separator} />
          </div>
          <div>
            <div>
              <Text
                as="span"
                variant="headingSm"
                tone="base"
                fontWeight="semibold"
              >
                2. Informe o código solicitado
              </Text>
              <Text
                as="p"
                variant="bodyXs"
                tone="supporting"
                fontWeight="medium"
                className={styles.stepSubtitle}
              >
                Digite o código abaixo:
              </Text>
            </div>
            <div className={styles.textContainer}>
              <Text fontSize="sm" fontWeight="medium" as="span">
                {proxy.proxyId}
              </Text>
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopyProxyId}
                aria-label="Copiar código solicitado"
              >
                <FontAwesomeIcon
                  icon={faClone}
                  color={color['color-base-border-2']}
                  fontSize="16px"
                />
              </button>
            </div>
            <div className={styles.separator} />
          </div>

          <div>
            <Text
              as="span"
              variant="headingSm"
              tone="base"
              fontWeight="semibold"
            >
              3. Aguarde a conexão
            </Text>
            <Text
              as="p"
              variant="bodyXs"
              tone="supporting"
              fontWeight="medium"
              className={styles.stepSubtitle}
            >
              A chamada será transferida para o cliente.
            </Text>
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="primary" size="xl" width="100%" onClick={onClose}>
            entendi
          </Button>
        </div>
      </div>
    </Modal>,
    document.body,
  );
}
